import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Owns the resend countdown shared by the verification, password-reset and
 * email-change screens.
 *
 * `start(seconds)` begins — or restarts — the timer. Call it after a successful
 * send with the server's `resend_available_in_seconds`, and after a 429 with the
 * response's `retryAfterSeconds`, so the button stays disabled for exactly as
 * long as the server insists. It replaces a running timer rather than adding to
 * it: a 429 that says "wait 90s" must win over a local timer that had 10s left.
 *
 * The interval is cleared in an effect, never inside the state updater, so the
 * hook is safe under StrictMode's double-invoked updaters.
 */
export function useResendCooldown(initialSeconds = 0) {
  const [secondsLeft, setSecondsLeft] = useState(() => Math.max(0, Math.ceil(initialSeconds)));
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(
    (seconds: number) => {
      clearTimer();
      const next = Math.max(0, Math.ceil(seconds));
      setSecondsLeft(next);
      if (next <= 0) {
        return;
      }
      intervalRef.current = setInterval(() => {
        setSecondsLeft((s) => Math.max(0, s - 1));
      }, 1000);
    },
    [clearTimer]
  );

  useEffect(() => {
    if (secondsLeft <= 0) {
      clearTimer();
    }
  }, [secondsLeft, clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  return { secondsLeft, start, isCoolingDown: secondsLeft > 0 };
}
